import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import UIContainer from 'components/ui/UIContainer';
import WithLoader from 'components/WithLoader';
import { axiosInstance } from 'setup/axiosInstance';
import {
  LearningMode,
  LearningSession,
  LearningSessionHistory,
} from 'app/types';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { useNavigate } from 'react-router-dom';
import LearningCoachHero from 'pages/Learning/components/LearningCoachHero';
import LearningContinueBanner from 'pages/Learning/components/LearningContinueBanner';
import LearningHowItWorks from 'pages/Learning/components/LearningHowItWorks';
import LearningModeGrid from 'pages/Learning/components/LearningModeGrid';
import LearningHistoryList from 'pages/Learning/components/LearningHistoryList';
import { MOCK_STATS } from 'pages/HomePage/mockStats';

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const HISTORY_PER_PAGE = 5;

const LearningPage: React.FC = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState<LearningSessionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingMode, setCreatingMode] = useState<LearningMode | null>(null);
  const [error, setError] = useState<string>('');
  const [historyPageNumber, setHistoryPageNumber] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(HISTORY_PER_PAGE);
  const [historyTotalElements, setHistoryTotalElements] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [confirmFreshOpen, setConfirmFreshOpen] = useState(false);
  const [sessionIdToDelete, setSessionIdToDelete] = useState<number | null>(
    null,
  );

  const inProgressSession = useMemo(
    () => history.find((s) => s.status === 'IN_PROGRESS'),
    [history],
  );
  const hasCompleted = useMemo(
    () => history.some((s) => s.status === 'DONE'),
    [history],
  );
  const isFirstTime = !history.length;

  const loadHistory = async (
    page = historyPageNumber,
    size = historyPageSize || HISTORY_PER_PAGE,
  ) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<
        PageResponse<LearningSessionHistory>
      >(`/learning-sessions?page=${page}&size=${size}`);
      setHistory(data.content);
      setHistoryPageNumber(data.number);
      setHistoryPageSize(data.size);
      setHistoryTotalElements(data.totalElements);
      setHistoryTotalPages(data.totalPages);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(0, HISTORY_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeHistoryPage = (page: number) => {
    loadHistory(page - 1, historyPageSize || HISTORY_PER_PAGE);
  };

  const openSession = (sessionId: number) => {
    navigate(
      fillUrlIds(pagePaths.learningSession, {
        learningSessionId: String(sessionId),
      }),
    );
  };

  const createSession = async (
    mode: LearningMode,
    options?: { wordCount?: number },
  ) => {
    if (mode === 'MANUAL') {
      // The "MANUAL" mode kicks off from word lists, not from here. Until the
      // FE has a dedicated picker, send the user to the dictionary so they can
      // start from the wordsets they actually have.
      navigate('/' + pagePaths.wordset);
      return;
    }
    setError('');
    setCreatingMode(mode);
    try {
      const { data } = await axiosInstance.post<LearningSession>(
        '/learning-sessions',
        { mode, source: 'HEADER', wordCount: options?.wordCount },
      );
      openSession(data.id);
    } catch {
      setError(
        'Не вдалось створити сесію. Перевір, чи є відповідні слова у Vocabulary.',
      );
    } finally {
      setCreatingMode(null);
    }
  };

  const deleteSession = async (sessionId: number) => {
    await axiosInstance.delete(`/learning-sessions/${sessionId}`);
    const remainingElements = Math.max(historyTotalElements - 1, 0);
    const lastPage = Math.max(
      Math.ceil(remainingElements / (historyPageSize || HISTORY_PER_PAGE)) - 1,
      0,
    );
    await loadHistory(Math.min(historyPageNumber, lastPage));
  };

  // When the user starts a fresh session from a mode card while another is
  // already IN_PROGRESS, we stash the picked mode here so the confirm dialog
  // can pick it up after the delete. Falls back to NEW_WORDS for legacy paths.
  const [pendingFreshMode, setPendingFreshMode] = useState<LearningMode | null>(
    null,
  );

  const handleStartFresh = async () => {
    if (!inProgressSession) {
      setConfirmFreshOpen(false);
      setPendingFreshMode(null);
      return;
    }
    const targetMode = pendingFreshMode ?? 'NEW_WORDS';
    await axiosInstance.delete(`/learning-sessions/${inProgressSession.id}`);
    setConfirmFreshOpen(false);
    setPendingFreshMode(null);
    await loadHistory(historyPageNumber);
    await createSession(targetMode);
  };

  // Wraps onStart for the mode grid: when a session is already in progress,
  // route the click through the confirm dialog instead of POSTing directly
  // (the BE would reject a second concurrent IN_PROGRESS).
  const handleModeGridStart = (mode: LearningMode) => {
    if (mode === 'MANUAL') {
      createSession(mode);
      return;
    }
    if (inProgressSession) {
      setPendingFreshMode(mode);
      setConfirmFreshOpen(true);
      return;
    }
    createSession(mode);
  };

  // Goal/streak — the BE doesn't expose these yet; pull from the same mock
  // source the home dashboard uses so the numbers line up across pages.
  const goal = MOCK_STATS.todayGoal;
  const streak = MOCK_STATS.streak.current;
  const recommendedMode: LearningMode = hasCompleted ? 'REPEAT' : 'NEW_WORDS';

  return (
    <>
      <UIContainer>
        <BreadCrumbs />
      </UIContainer>

      <UIContainer>
        <Box pb={4} pt={3}>
          <Stack spacing={2.5}>
            {error && <Alert severity={'warning'}>{error}</Alert>}

            <WithLoader loading={loading && !history.length}>
              <Stack spacing={2.5}>
                {inProgressSession ? (
                  <LearningContinueBanner
                    session={inProgressSession}
                    onContinue={() => openSession(inProgressSession.id)}
                    starting={creatingMode !== null}
                  />
                ) : isFirstTime ? (
                  <LearningHowItWorks
                    onStart={createSession}
                    starting={creatingMode !== null}
                  />
                ) : (
                  <LearningCoachHero
                    recommendedMode={recommendedMode}
                    goalTarget={goal.target}
                    goalDone={goal.current}
                    streak={streak}
                    onStart={createSession}
                    starting={creatingMode !== null}
                  />
                )}

                {!isFirstTime && (
                  <LearningModeGrid
                    label={
                      inProgressSession
                        ? 'Або обери інший режим'
                        : 'Або обери режим самостійно'
                    }
                    recommendedMode={recommendedMode}
                    onStart={handleModeGridStart}
                    starting={creatingMode !== null}
                    hasInProgressSession={!!inProgressSession}
                  />
                )}

                <LearningHistoryList
                  history={history}
                  page={historyPageNumber + 1}
                  totalPages={historyTotalPages}
                  onChangePage={changeHistoryPage}
                  onOpen={openSession}
                  onDelete={setSessionIdToDelete}
                />
              </Stack>
            </WithLoader>
          </Stack>
        </Box>
      </UIContainer>

      <Dialog
        disableScrollLock
        open={confirmFreshOpen}
        onClose={() => {
          setConfirmFreshOpen(false);
          setPendingFreshMode(null);
        }}
      >
        <DialogTitle>{'Почати нову сесію?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {
              'Поточна незавершена сесія буде видалена. Прогрес не збережеться в історії.'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmFreshOpen(false);
              setPendingFreshMode(null);
            }}
          >
            {'Скасувати'}
          </Button>
          <Button color={'error'} onClick={handleStartFresh}>
            {'Видалити і почати'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        disableScrollLock
        open={sessionIdToDelete !== null}
        onClose={() => setSessionIdToDelete(null)}
      >
        <DialogTitle>{'Видалити сесію?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {'Запис історії буде видалено. Слова у Vocabulary не зміняться.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant={'text'} onClick={() => setSessionIdToDelete(null)}>
            {'Скасувати'}
          </Button>
          <Button
            color={'error'}
            variant={'contained'}
            onClick={() => {
              if (sessionIdToDelete) deleteSession(sessionIdToDelete);
              setSessionIdToDelete(null);
            }}
          >
            {'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LearningPage;
